"use client";

import { Logo, Link } from "@/components";
import Image from "next/image";
import {
	HmcLabsCol,
	HmcLabsGrid,
	HmcLabsRow,
} from "@adi-ctx/harmonic-labs-react";
import { HmcButton } from "@adi-ctx/harmonic-core-components-react";
import "./Footer.scss";
import FooterUser from "./components/FooterUser/FooterUser";

// Images
import ezLogo from "/public/assets/icons/icon-ez.svg";
import linkedInIcon from "/public/assets/icons/icon-linkedin.svg";
import instagramIcon from "/public/assets/icons/icon-instagram.svg";
import youtubeIcon from "/public/assets/icons/icon-youtube.svg";
import facebookIcon from "/public/assets/icons/icon-facebook.svg";
import twitterIcon from "/public/assets/icons/icon-twitter.svg";
import shareXsIcon from "/public/assets/icons/share-xs.svg";
import t from "@/utils/translate.utils";

export default function Footer() {
	return (
		<footer data-testid="footer" className="footer c-white ">
			<section className="page-content page-content--no-padding lg:item-end flex flex-col justify-center py-6 lg:mx-auto lg:flex-row lg:justify-between lg:py-8">
				<HmcLabsGrid className="md:pl-0 md:pr-0">
					<HmcLabsRow>
						<HmcLabsCol size="12" size-lg="3" className="pb-8 pt-0 md:pl-0">
							<Logo isSameTab placement="footer" />
						</HmcLabsCol>

						<HmcLabsCol
							size="12"
							size-lg="3"
							className="footer__quick-links pr-3 lg:pr-12 lg:pt-0"
						>
							<h3
								className="hmc-subtitle-m mb-4 border-b border-b-white pb-4 leading-none"
								data-testid="footer-quick-links-title"
							>
								{t("footer.about-analog-devices")}
							</h3>
							<ul
								className="hmc-body-m list-none"
								data-testid="footer-quick-links"
							>
								<li>
									<Link
										isSameTab
										href="https://www.analog.com/en/who-we-are.html"
									>
										{t("footer.who-we-are")}
									</Link>
								</li>
								<li>
									<Link isSameTab href="https://www.analog.com/en/careers.html">
										{t("footer.careers")}
									</Link>
								</li>
								<li>
									<Link
										isSameTab
										href="https://www.analog.com/en/newsroom.html"
									>
										{t("footer.newsroom")}
									</Link>
								</li>
								<li>
									<Link isSameTab href="https://www.analog.com/en/signals.html">
										{t("footer.what-we-do")}
									</Link>
								</li>
								<li>
									<Link href="https://investor.analog.com/" className="flex">
										{t("footer.investor-relations")}
										<Image
											src={shareXsIcon}
											alt="(new browser tab)"
											data-testid="footer-external-link-icon"
											className="ml-1"
										/>
									</Link>
								</li>
								<li>
									<Link
										isSameTab
										href="https://www.analog.com/en/support/quality-and-reliability.html"
									>
										{t("footer.quality-reliability")}
									</Link>
								</li>
								<li>
									<Link
										isSameTab
										href="https://www.analog.com/en/who-we-are/corporate-information/sales-distribution.html"
									>
										{t("footer.sales-distribution")}
									</Link>
								</li>
								<li>
									<Link
										isSameTab
										href="https://www.analog.com/en/contact-us.html"
									>
										{t("footer.contact-us")}
									</Link>
								</li>
							</ul>
						</HmcLabsCol>

						<HmcLabsCol
							size="12"
							size-lg="3"
							className="footer__quick-links md:pr-12 lg:pt-0"
						>
							<h3
								className="hmc-subtitle-m mb-4 border-b border-b-white pb-4 leading-none"
								data-testid="footer-quick-links-title"
							>
								{t("footer.find-help")}
							</h3>
							<ul
								className="hmc-body-m list-none"
								data-testid="footer-quick-links"
							>
								<li>
									<Link isSameTab href="https://www.analog.com/en/support.html">
										{t("footer.support")}
									</Link>
								</li>
								<li>
									<Link href="https://ez.analog.com/" className="flex">
										{t("footer.engineer-zone")}
										<Image
											src={shareXsIcon}
											alt="(new browser tab)"
											data-testid="footer-external-link-icon"
											className="ml-1"
										/>
									</Link>
								</li>
								<li>
									<Link
										isSameTab
										href="https://www.analog.com/en/resources.html"
									>
										{t("footer.resources")}
									</Link>
								</li>
								<li>
									<Link href="https://wiki.analog.com/" className="flex">
										{t("footer.wiki")}
										<Image
											src={shareXsIcon}
											alt="(new browser tab)"
											data-testid="footer-external-link-icon"
											className="ml-1"
										/>
									</Link>
								</li>
								<li>
									<Link
										isSameTab
										href="https://www.analog.com/en/resources/analog-dialogue.html"
									>
										{t("footer.analog-dialogue")}
									</Link>
								</li>
							</ul>
						</HmcLabsCol>

						<HmcLabsCol size="12" size-lg="3" className="md:pr-0 lg:pt-0">
							<h3
								className="hmc-subtitle-m mb-4 border-b border-b-white pb-4 leading-none "
								data-testid="footer-quick-links-title"
							>
								{t("footer.my-analog")}
							</h3>
							<p className="hmc-body-s pb-4">{t("footer.description")}</p>
							<Link
								isSameTab
								href="https://my.analog.com/en/app/"
								data-testid="footer-myAnalog-button"
							>
								<HmcButton
									kind="tertiary"
									size="md"
									type="button"
									className="footer__myAnalog-button text-content"
								>
									<span className="footer-button">{t("login.button")}</span>
								</HmcButton>
							</Link>
						</HmcLabsCol>
					</HmcLabsRow>

					<HmcLabsRow className="mt-9">
						<HmcLabsCol
							push-lg="3"
							size="12"
							size-lg="9"
							className="flex flex-col md:pr-0 lg:flex-row lg:justify-between"
						>
							<div className="flex items-center pb-8 lg:pb-0">
								<FooterUser />
							</div>
							<ul className="footer__social flex items-center gap-6 lg:ml-auto lg:justify-end">
								<li slot="socialLinks">
									<a
										className="adiLink"
										href="https://ez.analog.com/"
										data-testid="footer-social-link-ez"
										target="_self"
									>
										<Image
											src={ezLogo}
											alt="EZ"
											data-testid="footer-social-img-ez"
										/>
									</a>
								</li>
								<li slot="socialLinks">
									<a
										className="adiLink"
										href="https://www.linkedin.com/company/analog-devices"
										data-testid="footer-social-link-linkedin"
										target="_self"
									>
										<Image
											src={linkedInIcon}
											alt="Linkedin"
											data-testid="footer-social-img-linkedin"
										/>
									</a>
								</li>
								<li slot="socialLinks">
									<a
										className="adiLink"
										href="https://www.instagram.com/analogdevices/?hl=en"
										data-testid="footer-social-link-instagram"
										target="_self"
									>
										<Image
											src={instagramIcon}
											alt="Instagram"
											data-testid="footer-social-img-instagram"
										/>
									</a>
								</li>
								<li slot="socialLinks">
									<a
										className="adiLink"
										href="https://www.youtube.com/c/analogdevicesinc"
										data-testid="footer-social-link-youtube"
										target="_self"
									>
										<Image
											src={youtubeIcon}
											alt="Youtube"
											data-testid="footer-social-img-youtube"
										/>
									</a>
								</li>
								<li slot="socialLinks">
									<a
										className="adiLink"
										href="https://www.facebook.com/AnalogDevicesInc"
										data-testid="footer-social-link-facebook"
										target="_self"
									>
										<Image
											src={facebookIcon}
											alt="Facebook"
											data-testid="footer-social-img-facebook"
										/>
									</a>
								</li>
								<li slot="socialLinks">
									<a
										className="adiLink"
										href="https://twitter.com/adi_news"
										data-testid="footer-social-link-twitter"
										target="_self"
									>
										<Image
											src={twitterIcon}
											alt="Twitter"
											data-testid="footer-social-img-twitter"
										/>
									</a>
								</li>
							</ul>
						</HmcLabsCol>
					</HmcLabsRow>

					<HmcLabsRow>
						<HmcLabsCol
							size="12"
							className="flex flex-col justify-between pt-9 md:pl-0 md:pr-0 lg:flex-row lg:items-center"
						>
							<ul className="mt-10 flex flex-col gap-8 whitespace-nowrap lg:mt-0 lg:flex-row lg:gap-6 lg:text-center">
								<li>
									<a
										href="https://www.analog.com/en/who-we-are/legal-and-risk-oversight.html"
										target="_self"
										data-testid="footer-shortLinks-legalAndRisk"
										className="hover:c-blue-900 text-sm hover:underline"
									>
										{t("footer.short-links.legal-risk")}
									</a>
								</li>
								<li>
									<a
										href="https://www.analog.com/en/who-we-are/legal-and-risk-oversight/accessibility-statement.html"
										target="_self"
										data-testid="footer-shortLinks-accessibility"
										className="hover:c-blue-900 text-sm hover:underline"
									>
										{t("footer.short-links.accessibility")}
									</a>
								</li>
								<li>
									<a
										href="https://www.analog.com/en/who-we-are/legal-and-risk-oversight/data-privacy/privacy-policy.html"
										target="_self"
										data-testid="footer-shortLinks-privacyPolicy "
										className="hover:c-blue-900 text-sm hover:underline"
									>
										{t("footer.short-links.privacy-policy")}
									</a>
								</li>
								<li>
									<a
										href="https://www.analog.com/en/lp/001/privacy-settings.html"
										target="_self"
										data-testid="footer-shortLinks-privacySettings"
										className="hover:c-blue-900 text-sm hover:underline"
									>
										{t("footer.short-links.privacy-settings")}
									</a>
								</li>
								<li>
									<a
										href="https://www.analog.com/en/cookie-notice.html"
										target="_self"
										data-testid="footer-shortLinks-cookieSettings"
										className="hover:c-blue-900 text-sm hover:underline"
									>
										{t("footer.short-links.cookie-settings")}
									</a>
								</li>
								<li>
									<a
										href="https://beian.miit.gov.cn/"
										target="_self"
										data-name="IPCLinkText"
										rel="noreferrer"
									>
										{t("footer.short-links.china-icp")}
									</a>
								</li>
							</ul>
							<p className="hmc-body-s mt-10 text-contentSilent lg:mt-0 lg:text-right">
								&copy;{new Date().getFullYear()} {t("footer.copyright")}
							</p>
						</HmcLabsCol>
					</HmcLabsRow>
				</HmcLabsGrid>
			</section>
		</footer>
	);
}
